
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { useAuth, SignUpResult } from "@/context/AuthContext";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { featureFlags } from "@/utils/featureFlags";

interface AuthFormProps {
  type: "sign-in" | "sign-up";
  onSubmitAttempt?: (email?: string) => void;
}

const AuthForm = ({ type, onSubmitAttempt }: AuthFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [signingInWithGoogle, setSigningInWithGoogle] = useState(false);
  
  const navigate = useNavigate();
  const { signIn, signUp, signInWithGoogle, isLoading } = useAuth();

  useEffect(() => {
    const googleAuthInProgress = sessionStorage.getItem('googleAuthInProgress');
    if (googleAuthInProgress) {
      console.log('Google auth in progress detected in AuthForm');
      setSigningInWithGoogle(true);
      
      const timeoutId = setTimeout(() => {
        if (sessionStorage.getItem('googleAuthInProgress')) {
          console.log('Resetting Google auth form state after timeout');
          setSigningInWithGoogle(false);
          sessionStorage.removeItem('googleAuthInProgress');
        }
      }, 10000); // Increased timeout for better reliability
      
      return () => clearTimeout(timeoutId);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Missing information", {
        description: "Please provide both email and password."
      });
      return;
    }
    
    try {
      if (type === "sign-in") {
        console.log('Form submission: Sign in with email', email);
        const success = await signIn(email, password);
        if (success) {
          toast.success("Signed in successfully!");
          navigate('/dashboard');
        }
      } else {
        if (!firstName) {
          toast.error("Missing information", {
            description: "Please provide your first name."
          });
          return;
        }
        console.log('Form submission: Sign up with email', email);
        
        const result = await signUp(email, password, firstName, lastName);
        console.log('Signup result:', result);
        
        if (result && typeof result === 'object') {
          if (result.requiresEmailConfirmation) {
            toast.success("Account created! Please check your email to confirm your account.", {
              duration: 8000
            });
            if (onSubmitAttempt) {
              onSubmitAttempt(email);
            }
          } else if (result.success) {
            toast.success("Account created successfully! You are now logged in.");
            navigate('/dashboard');
          }
        } else if (result === true) {
          toast.success("Account created successfully! You are now logged in.");
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error("Authentication error in form submission:", error);
    }
  };

  const handleGoogleSignIn = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (signingInWithGoogle || isLoading) {
      console.log("Already processing Google sign-in, ignoring click");
      return;
    }
    
    try {
      setSigningInWithGoogle(true);
      console.log("Starting Google sign-in flow from AuthForm");
      
      // Clear any stale state
      sessionStorage.removeItem('googleAuthInProgress');
      
      // Set fresh timestamp
      const timestamp = Date.now().toString();
      sessionStorage.setItem('googleAuthInProgress', timestamp);
      console.log("Set googleAuthInProgress with timestamp:", timestamp);
      
      // Initiate Google sign-in
      const success = await signInWithGoogle();
      
      if (!success) {
        console.log("Google sign-in initiation failed");
        setSigningInWithGoogle(false);
        sessionStorage.removeItem('googleAuthInProgress');
        return;
      }
      
      toast.info("Redirecting to Google...");
      
      // Set a timeout to reset the state if redirection takes too long
      const timeoutId = setTimeout(() => {
        console.log("Google sign-in button timeout - resetting state");
        setSigningInWithGoogle(false);
        // Don't remove the progress flag here since the auth flow might still be in progress
      }, 10000);
      
      return () => clearTimeout(timeoutId);
    } catch (error: any) {
      console.error("Error initiating Google sign-in from AuthForm:", error);
      toast.error("Google Sign-In Failed", {
        description: error.message || "We couldn't connect to Google. Please try again or use email sign-in."
      });
      setSigningInWithGoogle(false);
      sessionStorage.removeItem('googleAuthInProgress');
    }
  };

  return (
    <div className="w-full max-w-md mx-auto p-6 space-y-6 bg-white dark:bg-gray-900 rounded-xl shadow-sm animate-slide-up">
      <div className="text-center">
        <h2 className="text-2xl font-semibold mb-1">
          {type === "sign-in" ? "Welcome back" : "Create an account"}
        </h2>
        <p className="text-muted-foreground">
          {type === "sign-in" 
            ? "Enter your credentials to access your account" 
            : "Fill out the form below to get started"}
        </p>
      </div>
      
      {featureFlags.enableGoogleSignIn && (
        <>
          <Button 
            type="button" 
            variant="outline" 
            className="w-full flex items-center justify-center gap-2"
            onClick={handleGoogleSignIn}
            disabled={isLoading || signingInWithGoogle}
            id="googleSignInButton"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" className="text-inherit">
              <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z" />
                <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z" />
                <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z" />
                <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z" />
              </g>
            </svg>
            {signingInWithGoogle ? "Connecting to Google..." : isLoading ? "Processing..." : type === "sign-in" ? "Sign in with Google" : "Sign up with Google"}
          </Button>

          <div className="flex items-center gap-4 py-2">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground">OR</span>
            <Separator className="flex-1" />
          </div>
        </>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {type === "sign-up" && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="Enter your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="w-full"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Enter your last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        )}
        
        <div className="space-y-1">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full"
          />
        </div>
        
        <div className="space-y-1">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full pr-10"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        <Button 
          type="submit" 
          className="w-full" 
          disabled={isLoading}
          data-testid={type === "sign-up" ? "signup-button" : "signin-button"}
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {type === "sign-in" ? "Signing in..." : "Creating account..."}
            </span>
          ) : (
            <>{type === "sign-in" ? "Sign in" : "Create account"}</>
          )}
        </Button>
      </form>
    </div>
  );
};

export default AuthForm;
